import classNames from "classnames";
import { BaseButton } from "../../components/Button/BaseButton.tsx";
import { Button } from "../../components/Button/Button.tsx";
import { ThumbnailContent } from "../../components/Content/ThumbnailContent.tsx";
import { Icon } from "../../components/Icon/Icon.tsx";
import { trackEvent } from "../../tracking/tracking.ts";
import { computeDaysUntilDate } from "../../utils/date.ts";
import { CreditsAmountText } from "../Board/components/CreditsAmountText.tsx";
import { IMAGE_RESOURCE_TYPE } from "../Board/constants.ts";
import { MODEL_TYPE_LABELS } from "../Board/display.ts";
import { useRetrainModel } from "../Board/hooks/useRetrainModel.ts";
import { saveShoppingCart } from "../Board/utils/shoppingCart.ts";
import { PaymentDialog } from "../components/PaymentDialog/PaymentDialog.tsx";
import { upgradeDialogStore } from "../components/UpgradeDialog/upgradeDialogStore.ts";
import { CREDIT_PRICE, TRAINING_COST } from "../constants.ts";
import { useCreatePaymentCheckoutUrl } from "../hooks/useCreatePaymentCheckoutUrl.ts";
import { useWorkspace } from "../hooks/useWorkspace.ts";
import { useModel } from "./hooks/useModel.ts";
import { useRedirectToHomepageModels } from "./hooks/useRedirectToHomepageModels.ts";
import { useTrainModel } from "./hooks/useTrainModel.ts";

export const ModelTrainDialog = ({
  isOpen,
  onOpenChange,
  modelUuid,
  trainAction,
  onTrainModelSuccess,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  modelUuid: string;
  trainAction: "train" | "retrain";
  onTrainModelSuccess?: () => void;
}) => {
  const { workspace } = useWorkspace();

  const creditsToBuy = Math.max(
    TRAINING_COST - workspace.total_credits_remaining_amount,
    0,
  );

  return (
    <PaymentDialog
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      topSectionContent={<TopSectionContent />}
      midSectionContent={
        <MidSectionContent
          modelUuid={modelUuid}
          creditsToBuy={creditsToBuy}
          trainAction={trainAction}
          onTrainModelSuccess={onTrainModelSuccess}
          onOpenChange={onOpenChange}
        />
      }
      bottomSectionContent={<BottomSectionContent />}
      displayBottomSection={creditsToBuy > 0}
    />
  );
};

const TopSectionContent = () => {
  const { model } = useModel();

  return (
    model.contents.length >= 2 && (
      <div className="w-full flex-col items-center gap-400">
        <div className="h-600 px-200 py-100 rounded-full label-md-semibold bg-surface-caution-primary-rest">
          Ready in ~15 min
        </div>
        <div className="flex-fill h-[124px] flex-row gap-200">
          <PreviewImage
            url={model.contents[0].url}
            className="flex-fill -rotate-3 border-100 shadow-100"
          />
          <PreviewImage
            url={model.contents[1].url}
            className="flex-fill rotate-3 border-100 shadow-100"
          />
        </div>
      </div>
    )
  );
};

const PreviewImage = ({
  url,
  className,
}: {
  url: string;
  className?: string;
}) => (
  <ThumbnailContent
    resourceType={IMAGE_RESOURCE_TYPE}
    className={classNames(
      className,
      "h-full w-full overflow-hidden rounded-300",
    )}
    src={url}
  />
);

const MidSectionContent = ({
  modelUuid,
  creditsToBuy,
  trainAction,
  onTrainModelSuccess,
  onOpenChange,
}: {
  modelUuid: string;
  creditsToBuy: number;
  trainAction: "train" | "retrain";
  onTrainModelSuccess?: () => void;
  onOpenChange: (open: boolean) => void;
}) => {
  const { workspace } = useWorkspace();
  const { model } = useModel();

  const redirectToHomepageModels = useRedirectToHomepageModels(modelUuid);

  const price = Math.round(CREDIT_PRICE[workspace.type] * creditsToBuy) * 0.01;

  const daysBeforeRenewal = workspace.subscription_credits_next_added_at
    ? computeDaysUntilDate(
        Date.parse(workspace.subscription_credits_next_added_at),
      )
    : undefined;

  const { mutate: trainModel, isLoading: isTrainModelLoading } = useTrainModel({
    modelUuid,
    onTrainModelSuccess,
  });

  const { mutate: retrainModel, isLoading: isRetrainModelLoading } =
    useRetrainModel({
      modelUuid,
    });

  const { createPaymentCheckoutUrl, isCreatePaymentCheckoutUrlLoading } =
    useCreatePaymentCheckoutUrl();

  return (
    <div className="flex-col gap-600">
      <div className="heading-2xl">
        Train your {MODEL_TYPE_LABELS[model.type]}
      </div>
      <div className="flex-col gap-400">
        <div className="flex-col">
          <div
            className={classNames(
              "flex-row justify-between label-lg-default",
              creditsToBuy > 0 ? "text-error-secondary" : "text-secondary",
            )}
          >
            <div>Current balance</div>
            <CreditsAmountText
              creditsAmount={workspace.total_credits_remaining_amount}
            />
          </div>
          {creditsToBuy > 0 && daysBeforeRenewal !== undefined && (
            <div className="label-lg-default text-secondary">
              Renews in {daysBeforeRenewal} days
            </div>
          )}
        </div>
        <div className="flex-row justify-between label-lg-semibold">
          <div>1x Training cost</div>
          <CreditsAmountText creditsAmount={TRAINING_COST} />
        </div>
        {creditsToBuy > 0 && (
          <>
            <div className="h-[1px] w-full bg-surface-primary-hover" />
            <div className="flex-col gap-100 p-150 rounded-150 bg-surface-error-secondary-rest">
              <div className="flex-row gap-100 label-lg-default text-error-secondary">
                <div>Insufficient balance, buy</div>
                <CreditsAmountText creditsAmount={creditsToBuy} />
              </div>
              {["pro", "team"].includes(workspace.type) && (
                <div className="label-md-default text-secondary">
                  No immediate payment required: these additional credits will
                  be charged on your next invoice
                </div>
              )}
            </div>
          </>
        )}
      </div>
      <div className="flex-col gap-200">
        <Button
          loading={
            isTrainModelLoading ||
            isRetrainModelLoading ||
            isCreatePaymentCheckoutUrlLoading
          }
          onClick={() => {
            if (creditsToBuy > 0 && workspace.type === "basic") {
              saveShoppingCart({
                modelUuid,
                trainAction,
              });
              createPaymentCheckoutUrl({
                workspaceUuid: workspace.uuid,
                modelUuids: [modelUuid],
              });
            }
            //  FIXME: We should improve this flow for enterprise accounts (usage based billing...)
            else if (creditsToBuy > 0 && workspace.type === "enterprise") {
              window.open("mailto:contact@pimento.design", "_blank");
            } else {
              if (trainAction === "retrain") {
                retrainModel();
              } else {
                trainModel();
              }
            }
            onOpenChange(false);
          }}
        >
          {creditsToBuy > 0 ? (
            <div className="flex-row gap-100">
              <div>Buy</div>
              <CreditsAmountText creditsAmount={creditsToBuy} />
              <div>
                and train your {MODEL_TYPE_LABELS[model.type]} ($
                {price.toFixed(2)})
              </div>
            </div>
          ) : (
            <div>Train your {MODEL_TYPE_LABELS[model.type]}</div>
          )}
        </Button>
        <Button
          variant="tertiary"
          onClick={() => {
            redirectToHomepageModels();
          }}
        >
          Later, leave as draft
        </Button>
      </div>
    </div>
  );
};

const BottomSectionContent = () => {
  const { workspace } = useWorkspace();

  switch (workspace.type) {
    // FIXME: Improve UI / copy for enterprise account
    case "premium":
    case "team":
    case "enterprise":
      return (
        <>
          <div className="flex-col">
            <div className="label-lg-semibold">Need more out of Pimento?</div>
            <div className="body-md-default">
              We can tailor a plan specifically to your needs.
            </div>
          </div>
          <div className="flex-row items-start">
            <BaseButton
              className="body-md-semibold underline"
              onClick={() => {
                window.open("mailto:contact@pimento.design", "_blank");
              }}
            >
              Contact us
            </BaseButton>
          </div>
        </>
      );
    case "pro":
      return (
        <>
          <div className="flex-col">
            <div className="label-lg-semibold">Save 4$ on this model</div>
            <div className="body-md-default">
              Get 2000 monthly credits and train up to 100 models per month with
              Pimento Team.
            </div>
          </div>
          <div className="flex-row items-start">
            <Icon name="Sparkles" size="sm" />
            <BaseButton
              className="body-md-semibold underline"
              onClick={() => {
                trackEvent("train_model:upgrade_plan", {
                  workspace_type: workspace.type,
                });
                upgradeDialogStore.openDialog();
              }}
            >
              Upgrade plan
            </BaseButton>
          </div>
        </>
      );
    case "basic":
      return (
        <>
          <div className="flex-col">
            <div className="label-lg-semibold">Save 4$ on this model</div>
            <div className="body-md-default">
              Get 200 monthly credits and train up to 10 models per month with
              Pimento Pro.
            </div>
          </div>
          <div className="flex-row items-start">
            <Icon name="Sparkles" size="sm" />
            <BaseButton
              className="body-md-semibold underline"
              onClick={() => {
                trackEvent("train_model:upgrade_plan", {
                  workspace_type: workspace.type,
                });
                upgradeDialogStore.openDialog();
              }}
            >
              Upgrade plan
            </BaseButton>
          </div>
        </>
      );
  }
};
